import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Snackbar,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import FolderOpenOutlinedIcon from '@mui/icons-material/FolderOpenOutlined';
import {
  cloneCatalogTemplate,
  deleteCatalogTemplate,
  listCatalogTemplates,
} from '../../../app/api';
import { useTranslation } from '../../../app/LanguageContext';
import { usePageTitle } from '../../../layout/PageTitleContext';
import TemplateCard from '../private/TemplateCard';
import useCatalogTemplateDesigner from '../hooks/useCatalogTemplateDesigner';
import {
  folderDisplayName,
  openCatalogTemplateFolder,
} from '../private/desktopCatalogTemplateFiles';

const unwrapList = (res) => {
  const data = res?.data ?? res;
  return Array.isArray(data) ? data : [];
};

const TemplatesPage = () => {
  const navigate = useNavigate();
  const { translate } = useTranslation();
  const { setPageTitle } = usePageTitle();
  const isDesigner = useCatalogTemplateDesigner();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [fileEditBusy, setFileEditBusy] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: '' });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const templatesRes = await listCatalogTemplates();
      setTemplates(unwrapList(templatesRes));
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setPageTitle(translate('catalog.templates.list_title'));
    load();
  }, [load, setPageTitle, translate]);

  const handleEdit = (id) => {
    navigate(`/catalogs/templates/${id}/edit`);
  };

  const handleDelete = async (id, isSystem) => {
    if (isSystem) return;
    if (!window.confirm(translate('catalog.templates.confirm_delete'))) return;
    setBusyId(id);
    try {
      await deleteCatalogTemplate(id);
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusyId(null);
    }
  };

  const handleClone = async (id) => {
    setBusyId(id);
    try {
      const res = await cloneCatalogTemplate(id);
      const created = res?.data ?? res;
      if (created?.id) {
        navigate(`/catalogs/templates/${created.id}/edit`);
      } else {
        await load();
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setBusyId(null);
    }
  };

  const handleEditFile = async () => {
    setFileEditBusy(true);
    setError(null);
    try {
      const session = await openCatalogTemplateFolder();
      if (!session) {
        return;
      }
      if (session.unavailable) {
        throw new Error(translate('catalog.templates.edit_file_picker_unavailable'));
      }
      navigate('/catalogs/templates/edit-files', {
        state: {
          source: session.source,
          folderPath: session.folderPath,
          folderName: session.folderName || folderDisplayName(session.folderPath),
          html: session.html,
          css: session.css,
          dirHandle: session.dirHandle || null,
        },
      });
    } catch (e) {
      setSnack({ open: true, message: e.message });
    } finally {
      setFileEditBusy(false);
    }
  };

  return (
    <Box>
      {!isDesigner && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {translate('catalog.templates.designer_only')}
        </Alert>
      )}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Typography variant="h6">{translate('catalog.templates.list_title')}</Typography>
        {isDesigner && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Button
              variant="text"
              size="small"
              startIcon={<FolderOpenOutlinedIcon />}
              disabled={fileEditBusy}
              onClick={() => void handleEditFile()}
            >
              {translate('catalog.templates.edit_file')}
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/catalogs/templates/new')}
            >
              {translate('catalog.templates.create')}
            </Button>
          </Box>
        )}
      </Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      <Alert severity="info" sx={{ mb: 2 }}>
        {translate('catalog.templates.folder_hint')}
      </Alert>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : templates.length === 0 ? (
        <Typography color="text.secondary">{translate('catalog.templates.empty')}</Typography>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              lg: 'repeat(3, 1fr)',
            },
            gap: 2,
          }}
        >
          {templates.map((template) => (
            <TemplateCard
              key={template.id}
              template={template}
              busyId={busyId}
              onEdit={handleEdit}
              onClone={handleClone}
              onDelete={handleDelete}
            />
          ))}
        </Box>
      )}
      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        onClose={() => setSnack({ open: false, message: '' })}
        message={snack.message}
      />
    </Box>
  );
};

export default TemplatesPage;
